/**
 * Jump Navigation Component
 * IntersectionObserver-based active link tracking with smooth scroll
 */
(function () {
	"use strict";

	var CONFIG = {
		selectors: {
			nav: ".jump-nav",
			link: ".jump-nav__link",
			section: ".jump-section__section"
		},
		classes: {
			active: "jump-nav__link--active"
		},
		hashDelay: 100,
		observerThreshold: [0, 0.1, 0.25, 0.5, 0.75, 1]
	};

	var state = {
		dom: {},
		activeSection: null,
		observer: null
	};

	function isTouchNavMode() {
		return window.matchMedia("(pointer: coarse)").matches || window.innerWidth < 1335;
	}

	function getScrollOffset() {
		if (isTouchNavMode()) {
			var nav = document.querySelector(CONFIG.selectors.nav);
			if (nav) return nav.offsetHeight + 16;
			return 100;
		}
		return 100;
	}

	function syncScrollOffsetCSSVar() {
		var offset = getScrollOffset();
		document.documentElement.style.setProperty("--jump-scroll-offset", offset + "px");
		return offset;
	}

	function init() {
		if (!cacheDOMElements()) return;
		if (!state.dom.links || !state.dom.links.length || !state.dom.sections || !state.dom.sections.length) return;
		syncScrollOffsetCSSVar();
		setupEventListeners();
		initIntersectionObserver();
		handleInitialHash();
	}

	function cacheDOMElements() {
		state.dom = {
			nav: document.querySelector(CONFIG.selectors.nav),
			links: null,
			sections: null
		};
		if (!state.dom.nav) return false;
		state.dom.links = Array.from(state.dom.nav.querySelectorAll(CONFIG.selectors.link));
		state.dom.sections = Array.from(document.querySelectorAll(CONFIG.selectors.section));
		return true;
	}

	function setupEventListeners() {
		state.dom.links.forEach(function (link) {
			link.addEventListener("click", handleLinkClick);
		});
		window.addEventListener("resize", debounce(function () {
			syncScrollOffsetCSSVar();
			reinitObserver();
		}, 200));
	}

	function debounce(func, wait) {
		var timeout;
		return function () {
			var args = arguments;
			var context = this;
			var later = function () {
				clearTimeout(timeout);
				func.apply(context, args);
			};
			clearTimeout(timeout);
			timeout = setTimeout(later, wait);
		};
	}

	function reinitObserver() {
		if (state.observer) state.observer.disconnect();
		initIntersectionObserver();
	}

	function initIntersectionObserver() {
		var offset = syncScrollOffsetCSSVar();
		var topMargin = "-" + Math.max(0, offset) + "px";
		var options = {
			threshold: CONFIG.observerThreshold,
			rootMargin: topMargin + " 0px -50% 0px"
		};
		state.observer = new IntersectionObserver(handleIntersection, options);
		state.dom.sections.forEach(function (section) {
			state.observer.observe(section);
		});
	}

	function handleIntersection(entries) {
		var threshold = isTouchNavMode() ? 0.1 : 0.25;
		entries.forEach(function (entry) {
			if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
				updateActiveSection(entry.target);
			}
		});
	}

	function updateActiveSection(section) {
		if (state.activeSection === section) return;
		state.activeSection = section;
		var targetId = section ? section.id : null;
		if (!targetId) return;
		state.dom.links.forEach(function (link) {
			var href = link.getAttribute("href");
			var linkTarget = href ? href.substring(1) : "";
			if (linkTarget === targetId) {
				link.classList.add(CONFIG.classes.active);
			} else {
				link.classList.remove(CONFIG.classes.active);
			}
		});
	}

	function handleLinkClick(e) {
		e.preventDefault();
		var href = this.getAttribute("href");
		var targetId = href ? href.substring(1) : "";
		if (!targetId) return;
		var targetSection = document.getElementById(targetId);
		if (!targetSection) return;
		scrollToSection(targetSection, targetId);
	}

	function scrollToSection(section, id) {
		var offset = syncScrollOffsetCSSVar();
		var y = section.getBoundingClientRect().top + window.pageYOffset - offset;
		window.scrollTo({
			top: Math.max(0, y),
			behavior: "smooth"
		});
		history.pushState(null, "", "#" + id);
		updateActiveSection(section);
		// Move keyboard focus to the target section so screen readers and
		// keyboard users are placed at the destination, matching skip-link behaviour.
		if (!section.hasAttribute("tabindex")) {
			section.setAttribute("tabindex", "-1");
		}
		section.focus({ preventScroll: true });
	}

	function handleInitialHash() {
		var hash = window.location.hash;
		var targetId = hash ? hash.substring(1) : "";
		if (!targetId) return;
		var targetSection = document.getElementById(targetId);
		if (!targetSection) return;
		setTimeout(function () {
			scrollToSection(targetSection, targetId);
		}, CONFIG.hashDelay);
	}

	if (document.readyState !== "loading") {
		init();
	} else {
		document.addEventListener("DOMContentLoaded", init);
	}
})();
